import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Edit3, Plus, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

interface ConversationalInputProps {
  question: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  artifactType: 'big-sheet' | 'power-moves' | 'okrs' | 'opportunity';
  sectionType?: string;
  userContext?: string;
  customPrompt?: string; // New prop for handling quick prompts
  className?: string;
}

export const ConversationalInput: React.FC<ConversationalInputProps> = ({
  question,
  placeholder,
  value,
  onChange,
  artifactType,
  sectionType = 'default',
  userContext,
  customPrompt,
  className = ''
}) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const { toast } = useToast();

  const generateSuggestions = async () => {
    setIsLoadingSuggestions(true);
    try {
      const { data, error } = await supabase.functions.invoke('artifact-ai-assistant', {
        body: {
          prompt: question,
          artifactType,
          sectionType,
          userContext
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }
      
      if (data?.suggestions) {
        setSuggestions(data.suggestions);
        setShowSuggestions(true);
      } else {
        // Fallback to static suggestions if AI fails
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error generating suggestions:', error);
      // Fail gracefully - just hide suggestions instead of showing error
      setShowSuggestions(false);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  useEffect(() => {
    // For now, skip AI suggestions due to API key issues
    // generateSuggestions();
  }, [question, artifactType, sectionType, customPrompt]);

  // Generate new suggestions when a custom prompt is provided
  useEffect(() => {
    if (customPrompt) {
      generateCustomSuggestions(customPrompt);
    }
  }, [customPrompt]);

  const generateCustomSuggestions = async (promptText: string) => {
    setIsLoadingSuggestions(true);
    try {
      const { data, error } = await supabase.functions.invoke('artifact-ai-assistant', {
        body: {
          prompt: `User asked: "${promptText}". Provide 3-4 specific, editable examples they can quickly build from.`,
          artifactType,
          sectionType,
          userContext: `Quick prompt: ${promptText}`
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }
      
      if (data?.suggestions) {
        setSuggestions(data.suggestions);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error generating custom suggestions:', error);
      setShowSuggestions(false);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
  };

  const handleEdit = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
  };

  const handleCustomInput = () => {
    setShowSuggestions(false);
  };

  const handleRefresh = () => {
    generateSuggestions();
  };

  return (
    <Card className={`p-6 space-y-4 ${className}`}>
      <div className="space-y-3">
        <h3 className="text-lg font-medium text-foreground">{question}</h3>
        
        {(!showSuggestions || value) && (
          <div className="space-y-2">
            <Textarea
              placeholder={placeholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="min-h-[100px] resize-none"
              autoFocus={!showSuggestions}
            />
            {value && (
              <div className="flex justify-between items-center">
                <Badge variant="secondary" className="text-xs">
                  {value.length} characters
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Just clear the input for now
                    onChange('');
                  }}
                  className="text-xs"
                >
                  <Sparkles className="h-3 w-3 mr-1" />
                  Clear & restart
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};